import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Skeleton } from 'antd';
import ContentEntityHeaderComponent from '../../components/ContentEntityHeaderComponent';
import {
  ContentEntityActions,
  ContentEntityTypeActions,
  NarratorActions,
  createContentEntityMultilingual,
  editContentEntityMultilingual,
  getContentEntityDetailsByIdMultilingual,
  useContentSelector,
} from '@wajeez-web/stores/content';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from '@wajeez-web/stores/base';
import { useTranslation } from 'react-i18next';
import { WajeezTabsContainer } from '@wajeez-web/components/layouts';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { Server } from '@wajeez-web/core/api-base';
import { Languages } from '@wajeez-web/core/utils';

import CreateContentEntityBasicInfo from '../../components/CreateContentEntityBasicInfo';
import CreateContentEntityDetails from '../../components/CreateContentEntityDetails';
import ContentEntityCollectionComponent from '../../components/ContentEntityCollectionComponent';
import QuizzesPage from '../QuizzesPage';
import { QuizQuestionsPage } from '../QuizQuestionsPage';
import MultilingualAudioSegmentation from '../../components/MultilingualAudioSegmentation';
import { ContentEntitySEO } from '../../components/ContentEntitySEO';
import { useAuthSelector } from '@wajeez-web/stores/auth';
import { SwitchInput } from '@wajeez-web/components/ui';
import { ContentEntityArchive } from '../../components/ContentEntityArchive';

const CreateContentEntityPage = () => {
  const dispatch = useAppDispatch();
  const { type, id } = useParams() as { type: string; id: string };
  const navigate = useNavigate();

  const permissions = useAuthSelector((state) => state.auth.permission);

  const isLoading = useContentSelector(
    (s) => s.contentEntityType.status === 'loading'
  );
  const typeDetails = useContentSelector((s) => s.contentEntityType.details);
  const lookups = useContentSelector((s) => s.contentEntityType.lookups);
  const detailsByIdMultilingual = useContentSelector(
    (s) => s.contentEntity.detailsByIdMultilingual
  );

  const detailsByIdMultilingualData = detailsByIdMultilingual.data;

  const { t } = useTranslation();

  const languageId = useCommonSelector(
    (state) => state.common.settings.LanguageId
  );

  const createContentEntity = async (
    data: Server.CreateContentEntityByTypeIdV2Input
  ) => {
    createContentEntityMultilingual(data, (response) => {
      getContentEntityDetailsByIdMultilingual(response);

      navigate(`../../${response}/0`);
    });
  };

  const editContentEntity = async (
    data: Server.CreateContentEntityByTypeIdV2Input
  ) => {
    const mappedData = {
      ...data,
      profiles: data.profiles.map((p) => {
        const defaultNarrator = p.narratorAudios?.find((n) => n.isDefault);

        return {
          ...p,
          narratorAudios: p.narratorAudios?.map((n, i) => {
            if (i === 0 && !defaultNarrator) {
              return {
                ...n,
                isDefault: true,
              };
            } else {
              return n;
            }
          }),
        };
      }),
    };

    editContentEntityMultilingual({ ...mappedData, id: id }, (response) => {
      id && getContentEntityDetailsByIdMultilingual(id);
    });
  };

  const getContentEntityDetails = async () => {
    // dispatch(ContentEntityActions.getContentEntityByIdAsync(id));
    getContentEntityDetailsByIdMultilingual(id);
  };
  useEffect(() => {
    dispatch(
      ContentEntityTypeActions.getContentEntityTypeByNameAsync({ name: type })
    );
    dispatch(NarratorActions.getNarratorsLookUpsAsync(''));

    return () => {
      dispatch(ContentEntityTypeActions.clearDetails());
    };
  }, [type]);

  useEffect(() => {
    if (id) getContentEntityDetails();
    else dispatch(ContentEntityActions.clearDetails());
    return () => {
      dispatch(ContentEntityActions.clearDetails());
    };
  }, [id]);

  useEffect(() => {
    (async () => {
      if (id != undefined)
        await Promise.all([getContentEntityDetailsByIdMultilingual(id)]);
    })();

    return () => {
      dispatch(ContentEntityActions.clearDetailsByIdMultilingual());
    };
  }, [id]);

  return (
    <div className="mb-2">
      {isLoading ? (
        <div className="my-4">
          <Skeleton />
        </div>
      ) : (
        typeDetails && (
          <div>
            <WajeezTabsContainer
              headerComponent={
                <ContentEntityHeaderComponent
                  id={id}
                  type={type}
                  getContentEntityDetails={() => {
                    getContentEntityDetails();
                  }}
                  title={typeDetails.title || ''}
                  addedLanguages={
                    detailsByIdMultilingual?.data?.profiles?.map(
                      (s) => s.languageId as Languages
                    ) || []
                  }
                />
              }
              title={
                id
                  ? t(`general.${`Edit`}`, '') +
                    ' ' +
                    typeDetails.title +
                    ' "' +
                    (detailsByIdMultilingualData?.originalTitle ||
                      detailsByIdMultilingualData?.profiles?.find(
                        (p) => p.languageId === languageId
                      )?.title) +
                    ' "'
                  : t(`general.${`Create`}`, ' ') +
                    ' ' +
                    typeDetails.title +
                    ' '
              }
              tabs={[
                {
                  text: t(`general.${'Basic Info'}`, ''),
                  disabled: false,
                  content: () => (
                    <>
                      <CreateContentEntityBasicInfo
                        id={id}
                        type={typeDetails}
                        onSubmit={id ? editContentEntity : createContentEntity}
                        onCancel={() => {}}
                      />
                      {
                        typeDetails.allowedToBeArchived && (
                          <div className="mt-6">
                            <ContentEntityArchive 
                              archived={Boolean(detailsByIdMultilingualData?.archived)} 
                              id={id} 
                              onSuccess={() => {
                                getContentEntityDetails();
                              }}
                            />
                          </div>
                        )
                      }
                    </>
                  ),
                },
              ]
                .concat([
                  {
                    text: t(`general.${'Details'}`, ''),
                    disabled: id ? false : true,

                    content: () => (
                      <CreateContentEntityDetails
                        type={type}
                        requireAuthor={typeDetails.requireAuthor}
                        id={id}
                        allowedInEntityTypes={typeDetails.allowedInEntityTypes}
                      />
                    ),
                  },
                ])
                .concat([
                  {
                    text: t(`general.${'Marketing'}`, ''),
                    disabled:
                      id && permissions?.includes('156.1') ? false : true,
                    content: () => <ContentEntitySEO id={id} />,
                  },
                ])
                .concat(
                  typeDetails.allowedEntityTypes?.map((item, i) => ({
                    text:
                      lookups.find((s) => s.name === item.name)
                        ?.displayNamePlural ??
                      t(`general.${item.name}`, item.name || ''),
                    disabled: id ? false : true,
                    content: () => (
                      <ContentEntityCollectionComponent
                        id={id}
                        type={type}
                        key={(item?.name || '') + i}
                        data={lookups.find((s) => s.name === item.name) || item}
                        isPublished={
                          detailsByIdMultilingualData?.profiles?.find(
                            (p) => p.languageId === languageId
                          )?.published
                        }
                      />
                    ),
                  })) ?? []
                )
                .concat([
                  {
                    text: t(
                      `general.${'Manage Content Elements'}`,
                      'Manage Content Elements'
                    ),
                    disabled: false,
                    content: () => (
                      <ContentEntityCollectionComponent
                        id={id}
                        type={type}
                        showTypeOnItems
                        data={
                          {
                            name: 'CONTENTELEMENTS',
                            displayNameSingular: t(
                              `general.${'Manage Content Elements'}`,
                              'Manage Content Elements'
                            ),
                            displayNamePlural: t(
                              `general.${'Manage Content Elements'}`,
                              'Manage Content Elements'
                            ),
                            canBeAssignedWhenPublished: false,
                            canBeAssignedToWhenPublished: false,
                          } as Server.ContentEntityTypeLookUpWithFieldsOutput
                        }
                        isPublished={
                          detailsByIdMultilingualData?.profiles?.find(
                            (p) => p.languageId === languageId
                          )?.published
                        }
                      />
                    ),
                  },
                ])
                .concat(
                  id && typeDetails.isQuizable
                    ? [
                        {
                          text: t(
                            `general.${'Quiz Questions'}`,
                            'Quiz Questions'
                          ),
                          disabled: false,
                          content: () => <QuizQuestionsPage />,
                        },
                      ]
                    : []
                )
                .concat(
                  id && typeDetails.isQuizable
                    ? [
                        {
                          text: t(
                            `general.${'Quiz Questions'}`,
                            'Quiz Questions'
                          ),
                          disabled: false,
                          content: () => <QuizQuestionsPage />,
                        },
                      ]
                    : []
                )
                .concat(
                  type == 'BOOK'
                    ? [
                        {
                          text: 'تقسيم الفصول',
                          disabled: false,
                          content: () => (
                            <MultilingualAudioSegmentation type={typeDetails} />
                          ),
                        },
                      ]
                    : []
                )}
            />
          </div>
        )
      )}
    </div>
  );
};

export default CreateContentEntityPage;
